import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { SubscriptionChargeData } from '@/types/api';

const KEY = ['subscription-charges'];

export type ChargeStatusFilter = 'pending_review' | 'sent' | 'dismissed' | 'all';

export type SubscriptionChargesSummary = {
  pending_count: number;
  pending_cents: number;
  unlinked_count: number;
};

export function useSubscriptionCharges(status: ChargeStatusFilter) {
  return useQuery({
    queryKey: [...KEY, status],
    queryFn: async (): Promise<SubscriptionChargeData[]> => {
      const { data } = await api.get<SubscriptionChargeData[]>('/v1/subscription-charges', {
        params: { status },
      });
      return data;
    },
  });
}

export function useSubscriptionChargesSummary() {
  return useQuery({
    queryKey: [...KEY, 'summary'],
    queryFn: async (): Promise<SubscriptionChargesSummary> => {
      const { data } = await api.get<SubscriptionChargesSummary>(
        '/v1/subscription-charges/summary',
      );
      return data;
    },
  });
}

/** Drives the nav badge, so it stays cheap and separate from the list. */
export function usePendingReceiptsCount(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'pending-count'],
    enabled,
    queryFn: async (): Promise<number> => {
      const { data } = await api.get<{ count: number }>('/v1/subscription-charges/pending-count');
      return data.count;
    },
  });
}

/**
 * The receipt PDF as a blob object URL.
 *
 * `pdf_url` is a path, not a URL: the SPA is served from admin.<domain> while
 * the API answers on api.<domain>, so pointing an <object> straight at it
 * resolves against the SPA's own origin and lands on the router's 404 page.
 * Fetching through axios gets both the right host and the session cookie, the
 * same trick `openInvoicePdf` uses for invoices.
 */
export function useReceiptPdf(pdfUrl: string | null) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setObjectUrl(null);
    setFailed(false);

    if (!pdfUrl) return;

    let created: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const { data } = await api.get<Blob>(pdfUrl, { responseType: 'blob' });
        if (cancelled) return;
        created = URL.createObjectURL(data);
        setObjectUrl(created);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [pdfUrl]);

  return { objectUrl, failed };
}

/** Recipients and the admin's wording from the composer; blank wording keeps the standard text. */
type SendInput = { id: number; to?: string[]; cc?: string[]; subject?: string; message?: string };

export function useSendSubscriptionChargeReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: SendInput): Promise<SubscriptionChargeData> => {
      await primeCsrf();
      const { data } = await api.post<SubscriptionChargeData>(
        `/v1/subscription-charges/${id}/send-receipt`,
        body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

function useChargeAction(path: (id: number) => string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; body?: Record<string, unknown> }) => {
      await primeCsrf();
      const { data } = await api.post<SubscriptionChargeData>(path(input.id), input.body ?? {});
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDismissSubscriptionCharge() {
  return useChargeAction((id) => `/v1/subscription-charges/${id}/dismiss`);
}

export function useReopenSubscriptionCharge() {
  return useChargeAction((id) => `/v1/subscription-charges/${id}/reopen`);
}

export function useLinkSubscriptionChargeClient() {
  return useChargeAction((id) => `/v1/subscription-charges/${id}/client`);
}
